import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { translationsClient } from '../lib/translations';
import type { TranslationContext, TranslationResult } from '../lib/translations/types';
import { logger } from '../lib/logger';

export const useTranslatedContent = (context: TranslationContext) => {
  const [result, setResult] = useState<TranslationResult>({
    translations: {},
    loading: true,
    error: null
  });

  // Get general translations
  const { data: generalTranslations, isLoading: generalLoading } = useQuery({
    queryKey: ['translations', 'general'],
    queryFn: () => translationsClient.getGeneralTranslations(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Get category/type translations
  const { data: categoryTranslations, isLoading: categoryLoading } = useQuery({
    queryKey: ['translations', 'categories'],
    queryFn: () => translationsClient.getCategoryTypeTranslations(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Mémoriser les termes demandés pour éviter les changements inutiles
  const requestedTerms = context.terms || [];

  useEffect(() => {
    // Ne mettre à jour que lorsque les données sont chargées
    if (!generalLoading && !categoryLoading && generalTranslations && categoryTranslations) {
      // Merge translations
      const allTranslations = {
        ...generalTranslations,
        ...categoryTranslations
      };

      // Filter translations based on requested terms
      const filteredTranslations = requestedTerms.reduce((acc, term) => {
        if (allTranslations[term]) {
          acc[term] = allTranslations[term];
        }
        return acc;
      }, {} as Record<string, Record<string, string>>);

      setResult({
        translations: filteredTranslations,
        loading: false,
        error: null
      });

      logger.debug('Translations loaded', {
        category: 'Translation',
        data: { 
          requestedTerms,
          foundTerms: Object.keys(filteredTranslations)
        }
      });
    }
  }, [
    generalTranslations, 
    categoryTranslations, 
    generalLoading, 
    categoryLoading,
    // Utiliser la référence stable aux termes demandés
    JSON.stringify(requestedTerms)
  ]);

  return result;
};